import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star, Award, Target, Building2, Users, Heart, Send, ArrowRight, Calendar, MapPin,
  Shield, Briefcase, Wheat, GraduationCap, Hammer, Megaphone, Stethoscope, Scale, Leaf,
  Cpu, Flame, BookOpen, Globe, CheckCircle2, Vote, Wallet, Crown, Library, Gavel,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FourStars, StarsDivider } from "../components/FourStars";

const PHOTOS = {
  // New official premium photos
  diasporaEvent: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/un3bofmf_ABRAH%20ODIANCE.JPG",
  presidentialMission: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/t1xtyy1k_WhatsApp%20Image%202026-04-30%20at%2017.49.56.jpeg",
  bureau: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/w32wtzn3_BUREAU%20AFB.JPG",
  banniere2: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/z7yiome8_BANNIERE2.JPG",
  banniere5: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/nplw4r61_BANNIERE%205.JPG",
  banniere3: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/3cn3sivo_BANNIERE%203.JPG",
  banniere1: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/scki92mu_BANNIERE1.JPG",
  audience: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/4y1r0u17_ABRA%20%2B%20PRESIDENT.JPG",
  ministre: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/3p1c0gg5_ABRAHAM%20%2B%20MINISTRES.JPG",
  international: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/0o7lfgvn_ABRAH%20%2B%20SUMOI.JPG",
};

// ============================================================================
// Shared primitives
// ============================================================================
function PageHero({ overline, title, subtitle, image }) {
  return (
    <section className="relative py-24 md:py-32 text-white overflow-hidden">
      {image ? (
        <>
          <div className="absolute inset-0" style={{ backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44]/95 via-[#0A1F44]/80 to-[#0A1F44]/55" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1F44] via-[#0052CC] to-[#0A1F44]" />
      )}
      <div className="absolute inset-0 stars-pattern opacity-15" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
        <FourStars size={14} className="justify-center mb-4" />
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">{overline}</div>
        <h1 className="font-display text-4xl md:text-6xl mt-3 leading-tight">{title}</h1>
        {subtitle && <p className="text-white/85 mt-6 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">{subtitle}</p>}
        <div className="flex items-center justify-center gap-2 mt-8">
          <span className="w-10 h-1 bg-[#0052CC]" />
          <span className="w-10 h-1 bg-[#D4AF37]" />
          <span className="w-10 h-1 bg-[#CE1126]" />
        </div>
      </div>
    </section>
  );
}

function SectionTitle({ overline, title, center = false }) {
  return (
    <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
      {overline && <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">{overline}</div>}
      <h2 className="font-display text-3xl md:text-5xl text-[#0A1F44] mt-3 leading-[1.1]">{title}</h2>
    </div>
  );
}

function CheckList({ items, color = "#0052CC" }) {
  return (
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-[#1F2937]">
          <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color }} />
          <span className="text-sm md:text-base leading-relaxed">{it}</span>
        </li>
      ))}
    </ul>
  );
}

function CTABand({ title, text, primaryLabel, primaryTo, secondaryLabel, secondaryTo }) {
  return (
    <section className="py-20 bg-gradient-to-br from-[#0A1F44] to-[#0052CC] text-white">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <FourStars size={16} className="justify-center mb-5" />
        <h2 className="font-display text-3xl md:text-5xl">{title}</h2>
        {text && <p className="text-white/85 mt-4 leading-relaxed">{text}</p>}
        <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8">
          <Button asChild size="lg" className="bg-white hover:bg-white/95 text-[#0A1F44] rounded-full font-bold uppercase">
            <Link to={primaryTo}>{primaryLabel} <ArrowRight size={16} className="ml-2" /></Link>
          </Button>
          {secondaryLabel && (
            <Button asChild size="lg" className="border-2 border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1F44] rounded-full font-bold uppercase">
              <Link to={secondaryTo}>{secondaryLabel}</Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// 1) À PROPOS — Présentation de l'association
// ============================================================================
export function AProposPage() {
  const milestones = [
    { y: "2018", t: "Création", d: "Fondation de A.F.B. par Abraham Massala le 18 novembre 2018." },
    { y: "2019", t: "Élection présidentielle", d: "Soutien actif au candidat Félix-Antoine Tshisekedi Tshilombo." },
    { y: "2020+", t: "Évolution", d: "Le mouvement accompagne désormais les actions du Chef de l'État face aux défis du développement." },
    { y: "2026", t: "Mouvement national", d: "Présence active dans les 26 provinces de la RDC et 6 sections diaspora à travers le monde." },
  ];

  const stats = [
    { v: "26", l: "Provinces RDC couvertes" },
    { v: "6", l: "Sections diaspora actives" },
    { v: "8 ans", l: "D'engagement patriotique" },
    { v: "1", l: "Vision : un Congo Béton" },
  ];

  return (
    <div data-testid="apropos-page" className="bg-white">
      <PageHero
        overline="À propos"
        title="Les Amis de Fatshi Béton"
        subtitle="Une association de citoyens engagés au service du Président de la République et du peuple congolais."
        image={PHOTOS.diasporaEvent}
      />

      {/* Histoire & Fondation */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionTitle overline="Notre histoire" title="Un mouvement né d'une conviction patriotique" />
            <p className="text-[#475569] mt-6 leading-relaxed text-base md:text-lg">
              <strong>A.F.B.</strong> a été fondée le <strong className="text-[#0A1F44]">18 novembre 2018</strong> par son Président, Monsieur <strong className="text-[#0A1F44]">Abraham Massala</strong>.
              À l'origine, l'association avait pour mission de soutenir financièrement et matériellement la candidature de Félix Antoine Tshisekedi Tshilombo.
            </p>
            <p className="text-[#475569] mt-4 leading-relaxed text-base md:text-lg">
              Suite à son élection à la magistrature suprême de la République Démocratique du Congo, l'association a évolué pour <strong>accompagner les actions du Chef de l'État</strong> face aux défis du développement national.
            </p>
            <p className="text-[#475569] mt-4 leading-relaxed text-base md:text-lg">
              Aujourd'hui, A.F.B. est une organisation dynamique regroupant des <strong>acteurs engagés, intellectuels, professionnels et membres de la diaspora</strong>, œuvrant pour l'émergence d'un Congo nouveau, la mobilisation citoyenne et la solidarité nationale.
            </p>
          </div>
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            src={PHOTOS.audience}
            alt="A.F.B. avec le Président"
            className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
          />
        </div>
      </section>

      <StarsDivider />

      {/* Frise chronologique */}
      <section className="py-20 bg-[#F8F9FB]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Chronologie" title="Étapes clés du mouvement" center />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
            {milestones.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-black/5 shadow-sm p-7 relative"
                data-testid={`milestone-${i}`}
              >
                <div className="font-display text-3xl text-[#D4AF37] font-bold">{m.y}</div>
                <h3 className="font-display text-lg text-[#0A1F44] mt-2">{m.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{m.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-16 bg-gradient-to-r from-[#0A1F44] via-[#0052CC] to-[#0A1F44] text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <div className="font-display text-4xl md:text-5xl text-[#D4AF37] font-bold">{s.v}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <CTABand
        title="Découvrez notre vision"
        text="Mission, ambition, devise — comprenez ce qui nous anime."
        primaryLabel="Notre vision" primaryTo="/vision"
        secondaryLabel="Nos axes" secondaryTo="/axes"
      />
    </div>
  );
}

// ============================================================================
// 2) VISION & MISSION
// ============================================================================
export function VisionPage() {
  const visionPoints = ["Un Congo nouveau", "Une société inclusive", "Une nation forte et unie"];
  const beliefs = [
    "Chaque citoyen a sa place",
    "Chacun peut vivre dignement",
    "Tous contribuent au développement national",
  ];
  const objectives = [
    { icon: BookOpen, t: "Analyser les politiques publiques" },
    { icon: Briefcase, t: "Élaborer des stratégies d'accompagnement" },
    { icon: Megaphone, t: "Mobiliser les populations" },
    { icon: Globe, t: "Vulgariser les actions gouvernementales" },
    { icon: Send, t: "Assurer une communication institutionnelle" },
    { icon: Heart, t: "Soutenir les actions du Président" },
  ];

  return (
    <div data-testid="vision-page" className="bg-white">
      <PageHero
        overline="Vision · Mission · Ambition"
        title="Pour un Congo nouveau"
        subtitle="Notre vision politique, notre ambition citoyenne et notre devise patriotique."
        image={PHOTOS.presidentialMission}
      />

      {/* Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionTitle overline="Notre vision" title="Bâtir une nation moderne et prospère" />
            <p className="text-[#475569] mt-6 leading-relaxed text-base md:text-lg">
              Nous œuvrons pour la construction d'un Congo réuni, solidaire et tourné vers son développement économique, social et culturel.
            </p>
            <CheckList items={visionPoints} color="#0052CC" />
          </div>
          <div className="relative">
            <img src={PHOTOS.banniere5} alt="Vision A.F.B." className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-5 -left-5 bg-[#D4AF37] text-[#0A1F44] px-6 py-4 rounded-xl shadow-2xl">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold">Convictions</div>
              <div className="font-display text-xl mt-0.5">Un Congo Béton</div>
            </div>
          </div>
        </div>
      </section>

      <StarsDivider />

      {/* Convictions citoyennes */}
      <section className="py-20 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <SectionTitle overline="Nos convictions" title="Une société dans laquelle chaque citoyen…" center />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {beliefs.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-black/5 shadow-sm p-7"
                data-testid={`belief-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/15 grid place-items-center text-[#D4AF37] mx-auto">
                  <Star size={20} fill="#D4AF37" />
                </div>
                <p className="text-base text-[#0A1F44] mt-4 font-medium">{b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ambition */}
      <section className="py-20 bg-gradient-to-br from-[#0A1F44] to-[#0052CC] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Notre ambition</div>
          <h2 className="font-display text-3xl md:text-5xl mt-3">Comprendre · S'approprier · Accompagner</h2>
          <p className="text-white/85 mt-6 leading-relaxed max-w-3xl mx-auto text-base md:text-lg">
            Amener le peuple congolais à <strong className="text-[#D4AF37]">comprendre</strong>, <strong className="text-[#D4AF37]">s'approprier</strong> et <strong className="text-[#D4AF37]">accompagner</strong> la vision et les actions du Président de la République Démocratique du Congo.
          </p>
        </div>
      </section>

      {/* Devise */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FourStars size={18} className="justify-center mb-5" />
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] font-bold">Notre devise</div>
          <div className="font-display text-5xl md:text-7xl text-[#0A1F44] mt-6 leading-none">
            UNITÉ <span className="text-[#D4AF37]">·</span> PROGRÈS <span className="text-[#D4AF37]">·</span> TRAVAIL
          </div>
          <p className="text-[#475569] mt-8 italic max-w-xl mx-auto">
            « Trois mots, trois engagements, un seul peuple : le peuple congolais. »
          </p>
        </div>
      </section>

      {/* Objectifs */}
      <section className="py-20 bg-[#F8F9FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Nos objectifs" title="Six engagements concrets" center />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {objectives.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-black/5 shadow-sm p-7 hover:shadow-xl transition-all"
                data-testid={`objective-${i}`}
              >
                <div className="w-12 h-12 rounded-md bg-[#0052CC]/10 grid place-items-center text-[#0052CC]">
                  <o.icon size={20} />
                </div>
                <p className="font-display text-lg text-[#0A1F44] mt-4">{o.t}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Engagez-vous avec nous"
        primaryLabel="Devenir membre" primaryTo="/adhesion"
        secondaryLabel="Voir nos actions" secondaryTo="/actions"
      />
    </div>
  );
}

// ============================================================================
// 3) NOS AXES PRIORITAIRES
// ============================================================================
export function AxesPage() {
  const axes = [
    {
      number: "01", color: "#0052CC", icon: Shield, title: "Citoyenneté",
      tagline: "Un cadre de vie digne pour tous",
      points: [
        "Amélioration du cadre de vie urbain et rural",
        "Actions de salubrité et d'assainissement",
        "Sensibilisation contre les anti-valeurs (corruption, tribalisme, incivisme)",
      ],
      img: PHOTOS.banniere1,
    },
    {
      number: "02", color: "#D4AF37", icon: Briefcase, title: "Emploi",
      tagline: "Faire de la jeunesse une force productive",
      points: [
        "Formation professionnelle accélérée",
        "Création d'emplois durables",
        "Incubateur d'entreprises et accompagnement entrepreneurial",
        "Insertion socio-professionnelle des jeunes",
      ],
      img: PHOTOS.banniere2,
    },
    {
      number: "03", color: "#CE1126", icon: Wheat, title: "Souveraineté",
      tagline: "Manger congolais, produire congolais",
      points: [
        "Souveraineté alimentaire nationale",
        "Soutien à l'agriculture, l'élevage et la pêche",
        "Solutions locales durables et économies circulaires",
      ],
      img: PHOTOS.banniere5,
    },
  ];

  return (
    <div data-testid="axes-page" className="bg-white">
      <PageHero
        overline="Nos axes prioritaires"
        title="Trois piliers pour un Congo Béton"
        subtitle="Citoyenneté, emploi et souveraineté : les trois grands chantiers où nous investissons notre énergie."
        image={PHOTOS.banniere5}
      />

      <section className="py-20 space-y-20">
        {axes.map((a, i) => (
          <div key={i} className="max-w-7xl mx-auto px-6 md:px-10" data-testid={`axe-${i}`}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 ? "lg:[&>:first-child]:order-2" : ""}`}
            >
              <div>
                <div className="flex items-center gap-4">
                  <span className="font-display text-7xl font-bold leading-none" style={{ color: a.color }}>{a.number}</span>
                  <div className="w-12 h-12 rounded-md grid place-items-center" style={{ background: `${a.color}15`, color: a.color }}>
                    <a.icon size={22} />
                  </div>
                </div>
                <h2 className="font-display text-4xl md:text-5xl text-[#0A1F44] mt-5">{a.title}</h2>
                <p className="text-base md:text-lg italic text-[#475569] mt-3">{a.tagline}</p>
                <div className="mt-7 pt-6 border-t border-black/5">
                  <CheckList items={a.points} color={a.color} />
                </div>
              </div>
              <img src={a.img} alt={a.title} className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
            </motion.div>
            {i < axes.length - 1 && <StarsDivider className="mt-20" />}
          </div>
        ))}
      </section>

      <CTABand
        title="Soutenez nos axes prioritaires"
        text="Votre engagement contribue directement à transformer ces axes en réalités concrètes."
        primaryLabel="Faire un don" primaryTo="/dons"
        secondaryLabel="Devenir membre" secondaryTo="/adhesion"
      />
    </div>
  );
}

// ============================================================================
// 4) STATUTS — version structurée
// ============================================================================
export function StatutsPage() {
  const memberTypes = [
    { t: "Membres fondateurs", d: "Signataires de l'acte fondateur du 18 novembre 2018." },
    { t: "Membres actifs", d: "Engagés dans le fonctionnement régulier et les activités du mouvement." },
    { t: "Membres d'honneur", d: "Personnalités distinguées pour leur contribution exceptionnelle." },
    { t: "Membres sympathisants", d: "Soutiens occasionnels du mouvement et de ses actions." },
  ];
  const cotis = [
    { who: "Membres actifs", price: "20€", period: "/ mois", color: "#0052CC" },
    { who: "Membres bienfaiteurs", price: "240€", period: "/ an", color: "#D4AF37" },
    { who: "Membres d'honneur", price: "Exemptés", period: "Reconnaissance", color: "#CE1126" },
  ];
  const organes = [
    { icon: Vote, t: "Assemblée Générale", d: "Organe suprême — réunit l'ensemble des membres." },
    { icon: Library, t: "Conseil d'Administration", d: "Définit les orientations stratégiques du mouvement." },
    { icon: Crown, t: "Bureau Exécutif", d: "Met en œuvre la politique du mouvement au quotidien." },
    { icon: MapPin, t: "Antennes régionales", d: "Représentations territoriales en RDC et diaspora." },
  ];
  const bureau = [
    { role: "Président", icon: Crown },
    { role: "Vice-présidents", icon: Award },
    { role: "Secrétaire général", icon: BookOpen },
    { role: "Trésorier", icon: Wallet },
  ];
  const commissions = [
    { t: "Sociale", icon: Heart },
    { t: "Culturelle", icon: Flame },
    { t: "Développement", icon: Hammer },
    { t: "Jeunesse", icon: GraduationCap },
    { t: "Femmes", icon: Users },
    { t: "Économie & Entrepreneuriat", icon: Briefcase },
    { t: "Communication & Médias", icon: Megaphone },
    { t: "Santé", icon: Stethoscope },
    { t: "Juridique", icon: Scale },
    { t: "Environnement", icon: Leaf },
    { t: "Numérique", icon: Cpu },
    { t: "Mobilisation", icon: Send },
  ];
  const ressources = ["Cotisations des membres", "Dons et legs", "Subventions publiques et privées", "Activités économiques associatives", "Partenariats institutionnels"];

  return (
    <div data-testid="statuts-page" className="bg-white">
      <PageHero
        overline="Cadre juridique"
        title="Statuts de l'Association"
        subtitle="Le texte fondateur structurant la vie démocratique du mouvement A.F.B."
        image={PHOTOS.bureau}
      />

      {/* Création */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SectionTitle overline="Création" title="Une association loi 1901" />
            <p className="text-[#475569] mt-6 leading-relaxed text-base md:text-lg">
              Les Amis de Fatshi Béton (A.F.B.) sont constitués sous la forme d'une <strong>association loi 1901</strong>, créée le <strong className="text-[#0A1F44]">18 novembre 2018</strong>.
              L'association poursuit un but politique non lucratif : soutenir le Président de la République et œuvrer au développement du peuple congolais.
            </p>
          </div>
          <div className="bg-[#F8F9FB] rounded-2xl p-6 border-l-4 border-[#D4AF37]">
            <Building2 size={20} className="text-[#0052CC]" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-3">Siège social</div>
            <div className="font-display text-lg text-[#0A1F44] mt-1">10 Rue d'Artois</div>
            <div className="text-sm text-[#475569]">60400 Noyon, France</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-4">Date de fondation</div>
            <div className="font-display text-lg text-[#0A1F44]">18 novembre 2018</div>
          </div>
        </div>
      </section>

      <StarsDivider />

      {/* Membres */}
      <section className="py-20 bg-[#F8F9FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Article 1" title="Catégories de membres" center />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {memberTypes.map((m, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm" data-testid={`member-type-${i}`}>
                <Users size={20} className="text-[#0052CC]" />
                <h3 className="font-display text-lg text-[#0A1F44] mt-3">{m.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cotisations */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Article 2" title="Cotisations" center />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {cotis.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl p-8 border-2 text-center"
                style={{ borderColor: `${c.color}40` }}
                data-testid={`cotis-${i}`}
              >
                <div className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: c.color }}>{c.who}</div>
                <div className="font-display text-5xl text-[#0A1F44] mt-4">{c.price}</div>
                <div className="text-sm text-[#475569] mt-2">{c.period}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Organes */}
      <section className="py-20 bg-gradient-to-br from-[#0A1F44] to-[#0052CC] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Article 3</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3">Organes de gouvernance</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {organes.map((o, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 hover:border-[#D4AF37]/40 transition-all" data-testid={`organe-${i}`}>
                <div className="w-12 h-12 rounded-md bg-[#D4AF37]/15 grid place-items-center text-[#D4AF37]">
                  <o.icon size={20} />
                </div>
                <h3 className="font-display text-lg mt-4">{o.t}</h3>
                <p className="text-sm text-white/75 mt-2 leading-relaxed">{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bureau Exécutif */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Article 4" title="Bureau Exécutif" center />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12">
            {bureau.map((b, i) => (
              <div key={i} className="text-center" data-testid={`bureau-role-${i}`}>
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 grid place-items-center text-[#D4AF37] mx-auto">
                  <b.icon size={26} />
                </div>
                <div className="font-display text-base text-[#0A1F44] mt-3">{b.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commissions */}
      <section className="py-20 bg-[#F8F9FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Article 5" title="Les 12 commissions thématiques" center />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-12">
            {commissions.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="bg-white rounded-xl p-4 border border-black/5 shadow-sm flex items-center gap-3 hover:border-[#D4AF37]/40 transition-all"
                data-testid={`commission-${i}`}
              >
                <div className="w-9 h-9 rounded-md bg-[#0052CC]/10 grid place-items-center text-[#0052CC] shrink-0">
                  <c.icon size={16} />
                </div>
                <span className="text-sm font-medium text-[#0A1F44]">{c.t}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ressources */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Article 6" title="Ressources de l'association" />
          <div className="mt-8 bg-[#F8F9FB] rounded-2xl p-7 border-l-4 border-[#D4AF37]">
            <CheckList items={ressources} color="#D4AF37" />
          </div>
        </div>
      </section>

      <CTABand
        title="Adhérer en pleine connaissance"
        text="Vous connaissez désormais notre cadre juridique. Rejoignez le mouvement en toute confiance."
        primaryLabel="Devenir membre" primaryTo="/adhesion"
        secondaryLabel="Nous contacter" secondaryTo="/contact"
      />
    </div>
  );
}

// ============================================================================
// 5) ADHÉSION
// ============================================================================
export function AdhesionPage() {
  const benefits = [
    "Accès à votre espace membre privé",
    "Participation aux votes et sondages internes",
    "Invitations aux assemblées générales et événements",
    "Réception de la lettre d'information stratégique",
    "Reçu fiscal pour vos cotisations & dons",
    "Soutien direct aux actions présidentielles",
  ];
  const offers = [
    { color: "#0052CC", label: "Membre actif", price: "20€", period: "par mois", popular: false, link: "/dons" },
    { color: "#D4AF37", label: "Membre bienfaiteur", price: "240€", period: "par an", popular: true, link: "/dons" },
    { color: "#CE1126", label: "Membre d'honneur", price: "Sur invitation", period: "exempté de cotisation", popular: false, link: "/contact" },
  ];

  return (
    <div data-testid="adhesion-page" className="bg-white">
      <PageHero
        overline="Adhésion"
        title="Rejoignez le mouvement"
        subtitle="Engagez-vous concrètement aux côtés du Président et de millions de Congolais."
        image={PHOTOS.banniere2}
      />

      {/* Pourquoi adhérer ? */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionTitle overline="Pourquoi adhérer ?" title="Six bonnes raisons de nous rejoindre" />
            <div className="mt-8">
              <CheckList items={benefits} color="#D4AF37" />
            </div>
          </div>
          <img src={PHOTOS.banniere5} alt="Adhésion A.F.B." className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
        </div>
      </section>

      {/* Offres adhésion */}
      <section className="py-20 bg-[#F8F9FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Choisir sa formule" title="Trois niveaux d'engagement" center />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {offers.map((o, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className={`bg-white rounded-2xl p-8 border-2 relative ${o.popular ? "shadow-2xl scale-[1.03]" : "shadow-sm"}`}
                style={{ borderColor: o.popular ? o.color : `${o.color}30` }}
                data-testid={`offer-${i}`}
              >
                {o.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A1F44] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full">
                    ★ Populaire
                  </div>
                )}
                <div className="text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: o.color }}>{o.label}</div>
                <div className="font-display text-5xl text-[#0A1F44] mt-4">{o.price}</div>
                <div className="text-sm text-[#475569] mt-1">{o.period}</div>
                <Button asChild className="w-full mt-7 rounded-md font-bold uppercase tracking-wider" style={{ background: o.color, color: o.color === "#D4AF37" ? "#0A1F44" : "#fff" }}>
                  <Link to={o.link}>Choisir cette formule <ArrowRight size={14} className="ml-2" /></Link>
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionTitle overline="Comment ça marche ?" title="Adhésion en 3 étapes" center />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {[
              { n: "1", t: "Inscription", d: "Créez votre compte en 1 minute avec votre email ou via Google.", to: "/register" },
              { n: "2", t: "Cotisation", d: "Choisissez votre formule (mensuelle ou annuelle) — paiement Stripe sécurisé.", to: "/dons" },
              { n: "3", t: "Bienvenue", d: "Recevez un email de confirmation et accédez à votre espace membre.", to: "/dashboard" },
            ].map((s, i) => (
              <Link key={i} to={s.to} className="block bg-white rounded-2xl border border-black/5 shadow-sm p-7 hover:shadow-xl hover:border-[#D4AF37]/40 transition-all" data-testid={`step-${i}`}>
                <div className="font-display text-5xl text-[#D4AF37] font-bold leading-none">{s.n}</div>
                <h3 className="font-display text-xl text-[#0A1F44] mt-4">{s.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.d}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Prêt à vous engager ?"
        text="Cliquez ci-dessous et créez votre compte A.F.B. en 1 minute."
        primaryLabel="Devenir membre" primaryTo="/register"
        secondaryLabel="Faire un don" secondaryTo="/dons"
      />
    </div>
  );
}
